// Onboarding — supplier directory picker.
//
// Shown once to a not-yet-onboarded retailer: "which suppliers do you use?".
// The selection is saved server-side (POST /api/portal/onboarding/suppliers),
// which also queues an admin OUTREACH item for every off-platform supplier so
// The Kitchen OS team can contact them about a CSV / platform conversation.
// The selection is also mirrored into localStorage so the dashboard's
// "your suppliers" offer/news scoping picks it up immediately.

import { iconMarkup } from "@tkos/shared-ui";
import type { PageContext } from "./types";
import "./onboarding-suppliers.css";

const SHOWN_KEY = "kos_onboarding_suppliers"; // also read by dashboard connectedSupplierCodes()

interface DirEntry { code: string; name: string; category: string; onPlatform: boolean }

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}

function alreadyHandled(): boolean {
  try { return !!localStorage.getItem(SHOWN_KEY); } catch { return false; }
}

/** Show the supplier picker if this retailer hasn't onboarded and hasn't
 *  already made a selection. No-op otherwise. */
export function maybeShowSupplierOnboarding(ctx: PageContext): void {
  if (ctx.user?.onboardingComplete === true) return;
  if (alreadyHandled()) return;
  void openSupplierOnboarding(ctx);
}

/** Force-open the picker (e.g. from a "set up suppliers" link). */
export async function openSupplierOnboarding(ctx: PageContext): Promise<void> {
  if (document.getElementById("kos-onb-suppliers")) return;
  const retailerId = ctx.user?.retailerId ?? "";

  let directory: DirEntry[] = [];
  let selected = new Set<string>();
  try {
    const resp = await ctx.api<{ directory?: DirEntry[]; selected?: { code: string }[] }>(
      `/portal/onboarding/supplier-directory?retailerId=${encodeURIComponent(retailerId)}`,
      { method: "GET" },
    );
    directory = Array.isArray(resp?.directory) ? resp.directory : [];
    selected = new Set((resp?.selected ?? []).map((s) => s.code));
  } catch {
    return; // endpoint missing / not authed — don't block the user
  }
  if (directory.length === 0) return;

  const byCat = new Map<string, DirEntry[]>();
  for (const d of directory) {
    const list = byCat.get(d.category) ?? [];
    list.push(d);
    byCat.set(d.category, list);
  }

  const dialog = document.createElement("dialog");
  dialog.id = "kos-onb-suppliers";
  dialog.className = "kos-onb";
  const groups = Array.from(byCat.entries()).map(([cat, items]) => `
    <div class="kos-onb__group">
      <div class="kos-onb__group-title">${esc(cat)}</div>
      <div class="kos-onb__chips">
        ${items.map((d) => `
          <label class="kos-onb__chip${selected.has(d.code) ? " is-on" : ""}">
            <input type="checkbox" value="${esc(d.code)}" data-name="${esc(d.name)}" ${selected.has(d.code) ? "checked" : ""} />
            <span>${esc(d.name)}</span>
            ${d.onPlatform ? `<span class="kos-onb__live" title="Already on The Kitchen OS">Live</span>` : ""}
          </label>
        `).join("")}
      </div>
    </div>
  `).join("");

  dialog.innerHTML = `
    <form method="dialog" class="kos-onb__form">
      <header class="kos-onb__head">
        <div class="kos-onb__head-ico">${iconMarkup("package", { size: "md" })}</div>
        <div>
          <h2 class="kos-onb__title">Which suppliers do you use?</h2>
          <p class="kos-onb__sub">Pick the suppliers you work with. We'll tailor your catalogue and offers — and where a supplier isn't on the platform yet, we'll reach out to get them connected for you.</p>
        </div>
      </header>
      <div class="kos-onb__body">${groups}
        <div class="kos-onb__group">
          <div class="kos-onb__group-title">Add another</div>
          <input type="text" class="kos-onb__add" data-role="add" placeholder="Type a supplier name and press Enter" maxlength="60" />
          <div class="kos-onb__custom" data-role="custom"></div>
        </div>
      </div>
      <footer class="kos-onb__foot">
        <button type="button" class="kos-onb__btn" data-role="skip">Skip for now</button>
        <button type="button" class="kos-onb__btn kos-onb__btn--primary" data-role="save">Save my suppliers</button>
      </footer>
    </form>
  `;
  document.body.appendChild(dialog);

  const custom = new Map<string, string>(); // code -> name (free-typed)
  const customHost = dialog.querySelector<HTMLElement>("[data-role='custom']")!;
  const addInput = dialog.querySelector<HTMLInputElement>("[data-role='add']")!;
  const saveBtn = dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
  const skipBtn = dialog.querySelector<HTMLButtonElement>("[data-role='skip']")!;

  const renderCustom = (): void => {
    customHost.innerHTML = Array.from(custom.entries())
      .map(([code, name]) => `<span class="kos-onb__chip is-on" data-code="${esc(code)}"><span>${esc(name)}</span><button type="button" class="kos-onb__x" data-remove="${esc(code)}" aria-label="Remove">×</button></span>`)
      .join("");
  };

  addInput.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const name = addInput.value.trim();
    if (!name) return;
    const code = name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 24) || `S${custom.size + 1}`;
    custom.set(code, name);
    addInput.value = "";
    renderCustom();
  });
  customHost.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("[data-remove]");
    if (!btn) return;
    custom.delete(btn.dataset["remove"] || "");
    renderCustom();
  });

  // Toggle the is-on class as boxes change (visual only).
  dialog.querySelectorAll<HTMLInputElement>(".kos-onb__chip input[type=checkbox]").forEach((cb) => {
    cb.addEventListener("change", () => {
      cb.closest(".kos-onb__chip")?.classList.toggle("is-on", cb.checked);
    });
  });

  const close = (): void => { if (dialog.open) dialog.close(); dialog.remove(); };

  const markHandled = (payload: unknown): void => {
    try { localStorage.setItem(SHOWN_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
  };

  skipBtn.addEventListener("click", () => {
    // Record that we asked, so we don't nag every load.
    markHandled({ skipped: true, suppliers: [] });
    close();
  });

  saveBtn.addEventListener("click", () => {
    const picked: { code: string; name: string }[] = [];
    dialog.querySelectorAll<HTMLInputElement>(".kos-onb__chip input[type=checkbox]:checked").forEach((cb) => {
      picked.push({ code: cb.value, name: cb.dataset["name"] || cb.value });
    });
    for (const [code, name] of custom.entries()) picked.push({ code, name });

    saveBtn.disabled = true;
    void (async () => {
      try {
        const resp = await ctx.api<{ ok: boolean; outreachQueued?: string[] }>(
          "/portal/onboarding/suppliers",
          { method: "POST", body: JSON.stringify({ retailerId, selected: picked }) },
        );
        // Mirror to localStorage so the dashboard scopes "your suppliers" now.
        markHandled({ suppliers: picked.map((p) => p.code) });
        const queued = resp?.outreachQueued ?? [];
        ctx.showToast(
          queued.length
            ? `Saved. We'll reach out to ${queued.length} supplier${queued.length === 1 ? "" : "s"} to get them connected.`
            : "Suppliers saved",
          "success",
        );
        close();
      } catch {
        ctx.showToast("Couldn't save your suppliers — please try again.", "error");
        saveBtn.disabled = false;
      }
    })();
  });

  if (typeof dialog.showModal === "function") dialog.showModal();
  else dialog.setAttribute("open", "");
}
